import { ArrowRight01Icon, Sword03Icon } from '@hugeicons/react'

import { useNavigation } from '@/shared/hooks/useNavigation'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { GameIcon } from '@/shared/ui/GameIcon'
import { ProgressBar } from '@/shared/ui/ProgressBar'

import { useHomeView } from '../../hooks/useHomeView'
import { BlockEmptyState } from '../BlockEmptyState'
import { BlockSkeleton } from '../BlockSkeleton'
import { BlockGuidanceSheet } from '../guidance/ui/BlockGuidanceSheet'
import { GuidanceDoor } from '../guidance/ui/GuidanceDoor'
import { useBlockGuidance } from '../guidance/useBlockGuidance'

import type { BlockComponentProps } from '../index'

const DEFAULT_LIMIT = 3

/**
 * Próximas missões acionáveis. Consome `allNextMissions` de `useHomeView` — a
 * mesma derivação que alimenta o `QuestBookCard`, sem o corte fixo de três, para
 * o `settings.limit` do produtor valer.
 */
export const MissionsBlock = ({ block }: BlockComponentProps) => {
  const { navigateTo } = useNavigation()
  const { allNextMissions, isLoading } = useHomeView()
  const guidance = useBlockGuidance('missions')

  if (isLoading) return <BlockSkeleton rows={3} />

  // O vazio vem de `emptiness`, não de `allNextMissions.length`: aquela lista
  // exclui a missão promovida a destaque, e num evento com uma missão acionável
  // só ela fica vazia — o bloco anunciaria ausência com a missão visível acima.
  if (
    guidance.emptiness.kind === 'unconfigured' ||
    guidance.emptiness.kind === 'no-progress'
  )
    return (
      <>
        <BlockEmptyState
          icon={Sword03Icon}
          title="Nenhuma missão disponível"
          hint="Assim que houver missão para concluir, ela aparece aqui."
          action={
            guidance.hasGuidance ? (
              <GuidanceDoor
                label={guidance.doorLabel}
                onClick={guidance.onOpen}
              />
            ) : undefined
          }
        />

        <BlockGuidanceSheet
          open={guidance.open}
          onClose={guidance.onClose}
          title={guidance.sheetTitle}
          actions={guidance.actions}
        />
      </>
    )

  const limit = block.settings?.limit ?? DEFAULT_LIMIT
  const missions = allNextMissions.slice(0, limit)

  return (
    <Card className="card-gamification">
      <div className="flex items-center justify-between px-4 pb-0.5 pt-3">
        <h2 className="m-0 flex items-center gap-2 text-caps font-bold uppercase tracking-wide text-gray-700">
          <Sword03Icon size={14} variant="duotone" aria-hidden />
          {block.title || 'Próximas missões'}
        </h2>
        <Button
          variant="link"
          className="!no-underline text-xs font-bold"
          onClick={() => navigateTo('/missoes')}
          trailingIcon={<ArrowRight01Icon size={14} aria-hidden />}
        >
          Ver todas
        </Button>
      </div>

      <Card.Body className="flex flex-col gap-2 px-4 pb-3 pt-1.5">
        {missions.map(mission => (
          <button
            key={mission.key}
            type="button"
            onClick={() => navigateTo(mission.to)}
            className="flex w-full cursor-pointer items-center gap-3 rounded-[0.625rem] border-0 bg-[color-mix(in_srgb,var(--color-brand)_8%,white)] px-3 py-2 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--color-brand)_12%,white)]"
          >
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="truncate text-sm font-bold text-gray-900">
                {mission.title}
              </span>
              <span className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                <GameIcon name="star" size={10} />
                {mission.pointReward} XP
                {mission.isEndingSoon && (
                  <span className="text-warning-text">· termina em breve</span>
                )}
              </span>
              {mission.status === 'progress' && (
                <ProgressBar
                  value={mission.percent}
                  variant="primary"
                  size="sm"
                />
              )}
            </span>

            <ArrowRight01Icon
              size={16}
              className="shrink-0 text-gray-600"
              aria-hidden
            />
          </button>
        ))}
      </Card.Body>
    </Card>
  )
}
